import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { CartService } from '@core/services/cart.service';
import { FavoriteService } from '@core/services/favorite.service';
import { AuthService } from '@core/services/auth.service';
import { CatalogNavService } from '@core/services/catalog-nav.service';
import { StoreConfigService } from '@core/services/store-config.service';
import { SearchBarComponent } from '@shared/components/search-bar/search-bar.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { ReloadIfCurrentDirective } from '@shared/directives/reload-if-current.directive';
import { whatsappLink } from '@core/utils/whatsapp';

@Component({
  selector: 'app-header',
  imports: [RouterLink, SearchBarComponent, ReloadIfCurrentDirective],
  templateUrl: './header.component.html',
})
export class HeaderComponent {
  configService = inject(StoreConfigService);
  cart = inject(CartService);
  favorites = inject(FavoriteService);
  auth = inject(AuthService);

  private router = inject(Router);

  /** Qué hay publicado (compartido con el footer: una sola consulta por tienda). */
  private nav = inject(CatalogNavService);
  categories = this.nav.categories;
  hasCategories = this.nav.hasCategories;
  hasType(type: 'PRODUCT' | 'SERVICE' | 'KIT'): boolean {
    return this.nav.hasType(type);
  }

  /** Link de WhatsApp del negocio; sin número no se muestra el bloque de atención. */
  whatsappHref = computed(() => {
    return whatsappLink(this.configService.config().whatsappNumber);
  });

  private url = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** La cuenta es global (/auth/*): tras entrar se vuelve a esta página de la tienda. */
  currentUrl = this.url;

  /** Opción del menú que corresponde a la URL; "Productos" solo sin tipo (Kits/Servicios aparte). */
  activeNav = computed<'home' | 'products' | 'kits' | 'services' | 'categories' | null>(() => {
    const [path, query = ''] = this.url().split('?');
    const rest = path.replace(/^\/(tienda\/)?[^/]+/, '');
    const tipo = new URLSearchParams(query).get('tipo');
    if (rest === '' || rest === '/') return 'home';
    if (rest.startsWith('/categorias')) return 'categories';
    if (rest === '/productos') return tipo === 'KIT' ? 'kits' : tipo === 'SERVICE' ? 'services' : 'products';
    return null;
  });

  mobileMenuOpen = signal(false);
  browseCategoriesOpen = signal(false);
  userMenuOpen = signal(false);

  navClass(key: string): string {
    return (
      'transition-colors py-1 border-b-2 ' +
      (this.activeNav() === key ? 'border-white text-white' : 'border-transparent hover:text-white/80')
    );
  }

  logout(): void {
    this.auth.logout();
    this.userMenuOpen.set(false);
  }
}
